import { useQuery } from '@apollo/client';
import { Layout } from '@components/Layout';
import type { NestedSidebarItem } from '@components/SidebarV2';
import { NestedSidebar } from '@components/SidebarV2';
import PermissionGate from '@components/upercut-permissions/PermissionGate';
import { GET_PRO } from '@gql/pros';
import CompanyBrands from '@modules/companies/CompanyBrands';
import CompanyEdit from '@modules/companies/CompanyEdit';
import CompanyJobApplications from '@modules/companies/CompanyJobApplications';
import CompanyJobOffers from '@modules/companies/CompanyJobOffers';
import CompanyList from '@modules/companies/CompanyList';
import CompanyBanner from '@modules/companies/components/CompanyBanner';
import ProfileSkeleton from '@modules/talents/components/ProfileSkeleton';
import { Box } from '@mui/material';
import { Stack } from '@mui/system';
import { useMemo } from 'react';
import { Navigate, Outlet, Route, Routes, useParams } from 'react-router-dom';
import OrganizationUserPage from '@pages/OrganizationUsers/OrganizationUserPage';
import CompanySubscriptionPlans from '@modules/companies/CompanySubscriptionPlans';
import CompanyFeatures from '@modules/companies/CompanyFeatures';
import CompanyJobOfferTemplates from '@modules/companies/CompanyJobOfferTemplates';

const CompanyPage = () => {
    return (
        <Layout
            breadcrumbs={[
                { label: 'Tableau de bord', href: '/', value: 'dashboard' },
                { label: 'Liste des Établissements', href: '/companies', value: 'companies' },
                { label: "Détail de l'Établissement", href: '/companies/:id/edit', value: 'edit' },
                {
                    label: "Candidatures de l'Établissement",
                    href: '/companies/:id/applications',
                    value: 'applications',
                },
                {
                    label: "Offres d'emploi de l'Établissement",
                    href: '/companies/:id/job-offers',
                    value: 'job-offers',
                },
                {
                    label: "Marques de l'Établissement",
                    href: '/companies/:id/brands',
                    value: 'brands',
                },
                {
                    label: "Utilisateurs de l'organisation",
                    href: '/companies/:id/users',
                    value: 'users',
                },
                {
                    label: "Abonnements de l'Établissement",
                    href: '/companies/:id/subscription-plans',
                    value: 'subscription-plans',
                },
                {
                    label: "Fonctionnalités de l'Établissement",
                    href: '/companies/:id/features',
                    value: 'features',
                },
                {
                    label: "Modèles d'offres de l'Établissement",
                    href: '/companies/:id/job-offer-templates',
                    value: 'job-offer-templates',
                },
            ]}
            heading="Gestion des Établissements">
            <Routes>
                <Route
                    path="/"
                    element={
                        <PermissionGate permission="companies:read">
                            <CompanyList />
                        </PermissionGate>
                    }
                />
                <Route path="/:id" element={<CompanyDetailsLayout />}>
                    <Route index element={<Navigate to="edit" replace />} />
                    <Route
                        path="edit"
                        element={
                            <PermissionGate permission="companies:update">
                                <CompanyEdit />
                            </PermissionGate>
                        }
                    />
                    <Route
                        path="applications"
                        element={
                            <PermissionGate permission="job-applications:read">
                                <CompanyJobApplications />
                            </PermissionGate>
                        }
                    />
                    <Route
                        path="job-offers"
                        element={
                            <PermissionGate permission="job-offers:read">
                                <CompanyJobOffers />
                            </PermissionGate>
                        }
                    />
                    <Route
                        path="brands"
                        element={
                            <PermissionGate permission="brands:read">
                                <CompanyBrands />
                            </PermissionGate>
                        }
                    />
                    <Route path="users/*" element={<OrganizationUserPage />} />
                    <Route
                        path="subscription-plans"
                        element={
                            <PermissionGate permission="subscriptions:read">
                                <CompanySubscriptionPlans />
                            </PermissionGate>
                        }
                    />
                    <Route
                        path="features"
                        element={
                            <PermissionGate permission="companies:manage">
                                <CompanyFeatures />
                            </PermissionGate>
                        }
                    />
                    <Route
                        path="job-offer-templates"
                        element={
                            <PermissionGate permission="job-offer-templates:read">
                                <CompanyJobOfferTemplates />
                            </PermissionGate>
                        }
                    />
                </Route>
                <Route path="/:id/*" element={<CompanyIdFallbackRedirect />} />
            </Routes>
        </Layout>
    );
};

const CompanyIdFallbackRedirect = () => {
    const { id } = useParams<{ id: string }>();

    if (!id) {
        return <Navigate to="/companies" replace />;
    }

    return <Navigate to={`/companies/${id}/edit`} replace />;
};

const CompanyDetailsLayout = () => {
    const { id } = useParams<{ id: string }>();

    const { data: proData, loading: loadingProData } = useQuery(GET_PRO, {
        variables: { proId: id },
        skip: !id,
    });

    const companyData = useMemo(() => proData?.getPro, [proData]);

    const nestedItems: NestedSidebarItem[] = useMemo(
        () => [
            { id: 'edit', label: 'Modifier', icon: 'fi-br-info', path: `/companies/${id}/edit` },
            { id: 'applications', label: 'Candidatures', icon: 'fi-br-inbox', path: `/companies/${id}/applications` },
            {
                id: 'job-offers',
                label: "Offres d'emploi",
                icon: 'fi-br-briefcase',
                path: `/companies/${id}/job-offers`,
            },
            {
                id: 'job-offer-templates',
                label: "Modèles d'offres",
                icon: 'fi-br-document',
                path: `/companies/${id}/job-offer-templates`,
            },
            { id: 'brands', label: 'Marques', icon: 'fi-br-copyright', path: `/companies/${id}/brands` },
            { id: 'users', label: 'Utilisateurs', icon: 'fi-br-users', path: `/companies/${id}/users` },
            {
                id: 'subscription-plans',
                label: 'Abonnements',
                icon: 'fi-br-credit-card',
                path: `/companies/${id}/subscription-plans`,
            },
            {
                id: 'features',
                label: 'Fonctionnalités',
                icon: 'fi-br-settings-sliders',
                path: `/companies/${id}/features`,
            },
        ],
        [id],
    );

    if (loadingProData) {
        return (
            <Stack sx={{ pt: 0, flex: 1, minHeight: '100%', gap: 3 }}>
                <ProfileSkeleton />
            </Stack>
        );
    }

    return (
        <Box sx={{ display: 'flex', flex: 1, minHeight: '100%', mx: '-24px' }}>
            <NestedSidebar items={nestedItems} title="Établissement" />
            <Stack flex={1} width={0} pt={0} minHeight="100%">
                <Stack sx={{ gap: 3, mb: 3, px: 3 }}>
                    <CompanyBanner pro={companyData} editable />
                </Stack>
                <Box sx={{ flex: 1, pb: 3, display: 'flex', px: '24px', minWidth: 0 }}>
                    <Outlet />
                </Box>
            </Stack>
        </Box>
    );
};

export default CompanyPage;
